import styled, { css } from 'styled-components'
import { COLORS } from 'consts/colors'
import Popup from 'common-components/Popup'
import AiSparklesSvg from 'assets/svgr-icons/AiSparkles.svg'

import RemoveSvg from 'assets/svgr-icons/Remove.svg'
import FitSvg from 'assets/svgr-icons/Fit.svg'
import UndoSvg from 'assets/svgr-icons/Undo.svg'
import PreviewSvg from 'assets/svgr-icons/Preview.svg'

const actionIcon = css`
  width: 14px;
  height: 14px;

  path {
    fill: ${COLORS.secondaryDark};
  }
`

export const DeleteIcon = styled(RemoveSvg)`
  ${actionIcon}
  width: 12px;
  height: 12px;
`

export const ResizeIcon = styled(FitSvg)`
  ${actionIcon}
`

export const EnhanceIcon = styled(AiSparklesSvg)`
  ${actionIcon}
`

export const UndoIcon = styled(UndoSvg)`
  ${actionIcon}
`

export const PreviewIcon = styled(PreviewSvg)`
  ${actionIcon}
`

// Large-image popup for the Preview action: the shared Popup shell (backdrop,
// Esc, close icon) with tighter padding so the image dominates.
export const PreviewPopup = styled(Popup)`
  .popup-content {
    padding: 32px;
    border-radius: 16px;
  }
`

export const PreviewImage = styled.img<{ $aspectRatio?: number }>`
  display: block;
  max-width: 100%;
  /* The popup content is capped at 90% viewport height with 32px padding on
     BOTH sides (see PreviewPopup) — the cap must subtract both, or a portrait
     (9:16) image overflows the popup's bottom edge. Wide images are safe via
     max-width (the popup's own 90% width cap). */
  max-height: calc(90vh - 64px);
  object-fit: contain;
  border-radius: 8px;

  /* max-* only ever SHRINKS — a low-resolution source (e.g. a 250×379 upload)
     renders at its tiny natural size. When the natural dimensions are known
     (uploaded / My-Files avatars) drive the height explicitly so small
     originals scale UP to fill the popup: the height cap, or the width budget
     (90vw popup minus 2×32px padding) converted through the image's own
     ratio — whichever binds first, so neither axis overflows. Dimension-less
     avatars (public presets) keep the natural-size path above; those files
     are high-res and already fill the cap. */
  ${({ $aspectRatio }) =>
    $aspectRatio &&
    css`
      height: min(calc(90vh - 64px), calc((90vw - 64px) / ${$aspectRatio}));
      width: auto;
    `}
`

// Mobile variant of the Preview action: the image in a bottom sheet — the
// same mobile idiom as the Resize/Enhance panels (SlideUp above the 64px
// bottom tab bar), so the image gets the full viewport width.
export const PreviewSlideUpBody = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 16px 16px;
`

export const PreviewSlideUpImage = styled(PreviewImage)`
  /* Fit inside the sheet instead of the popup's near-full-viewport cap:
     62vh normally, but never more than the sheet's own max-height
     (100% - 148px) minus its padding/drag-line (~48px) — on short viewports
     (landscape phones) 62vh alone would overflow the sheet like the 9:16
     popup case. */
  max-height: min(62vh, calc(100vh - 196px));

  /* Same upscale rule as PreviewImage, with the sheet's own caps: the sheet
     is full-width with 16px body padding per side (100vw - 32px). */
  ${({ $aspectRatio }) =>
    $aspectRatio &&
    css`
      height: min(
        62vh,
        calc(100vh - 196px),
        calc((100vw - 32px) / ${$aspectRatio})
      );
    `}
`

// Top-left overlay slot for the Undo button — the positional mirror of the
// top-right `ActionsRow` (avatar-preview/styled.ts), anchored to the same
// relative `PreviewContainer`.
export const UndoSlot = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
`
